import { Box, Button, Drawer } from '@mui/material';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import BackButton from 'components/@home/drawers/BackButton';
import CloseButton from 'components/@home/drawers/CloseButton';
import { HrBotContext } from 'components/@home/drawers/HrBotDrawer/hrBotContext';
import HrBotTab from 'components/@home/drawers/HrBotDrawer/HrBotTab';
import Loading from 'components/common/Loading';
import KeyboardArrowRightIcon from 'mdi-react/KeyboardArrowRightIcon';
import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { HR_BOT_APP_ID } from 'services/constants';
import apps from 'store/app/entities/apps/action';
import bulkMessagings from 'store/app/entities/bulkMessaging/action';
import documentFormats from 'store/app/entities/documentFormats/action';
import hrBotDrawer from 'store/app/ui/drawers/hrBot/action';
import useActions from 'utils/useActions';

const HrBotDrawer = () => {
  const isOpen = useSelector(state => state.drawers.hrBot.isOpen);
  const dispatch = useDispatch();
  const app = useSelector(state => state.apps.apps[HR_BOT_APP_ID]);
  const bulkMessaging = useSelector(state => state.bulkMessaging.bulkMessaging);
  const { formats, loaded: formatsLoaded } = useSelector(state => state.documentFormats);
  const [files, setFiles] = useState(null);
  const [formatId, setFormatId] = useState(null);
  const [message, setMessage] = useState('');
  const [uploadedPct, setUploadedPct] = useState('');
  const format = useMemo(() => formats[formatId], [formats, formatId]);
  const [tabStack, setTabStack] = useState(['home']);
  const [currentFile, setCurrentFile] = useState(null);
  const [droppedFiles, setDroppedFiles] = useState({});
  const checkIds = useActions(bulkMessagings.checkIds);

  const navigateTo = useCallback(tab => {
    setTabStack(ts => [...ts, tab]);
  }, []);

  const tab = useMemo(() => tabStack[tabStack.length - 1], [tabStack]);

  useEffect(() => {
    if (isOpen) {
      setFiles(null);
      setFormatId(null);
      setMessage('');
      setUploadedPct('');
      setTabStack(['home']);
      setCurrentFile(null);
      setDroppedFiles({});
      dispatch(bulkMessagings.clear());
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (tabStack.length === 1) {
      setFiles(null);
      setFormatId(null);
      setMessage('');
      setUploadedPct('');
      setCurrentFile(null);
      setDroppedFiles({});
      dispatch(bulkMessagings.clear());
    }
  }, [tabStack.length, dispatch]);

  const navigateBack = useCallback((howMany = 1) => {
    setTabStack(ts => {
      const newStack = ts.slice(0, typeof howMany === 'number' ? -howMany : -1);
      if (newStack.length === 1) {
        setFiles(null);
      }
      return newStack;
    });
  }, []);

  useEffect(() => {
    if (!app && isOpen) {
      dispatch(apps.findHrBot());
    }
  }, [app, dispatch, isOpen]);

  useEffect(() => {
    if (!formatsLoaded && isOpen) {
      dispatch(documentFormats.load());
    }
  }, [dispatch, formatsLoaded, isOpen]);

  useEffect(() => {
    setMessage(format?.defaultMessage || I18n.t('HrBot.defaultMessage'));
  }, [format?.defaultMessage, formatId]);

  const title = useMemo(
    () =>
      tabStack.map((t, idx) => {
        const isPast = idx < tabStack.length - 1;
        const titleStr = (
          <Typography
            key={t}
            component="h1"
            variant="h5"
            sx={[
              {
                display: 'inline',
              },
              { m: '20px 0', height: 'auto' },
              isPast && { color: 'secondary.main', fontWeight: 'normal' },
            ]}
          >
            {I18n.t(`HrBot.tabs.${!format && t === 'editTemplate' ? 'newTemplate' : t}.title`, {
              appName: app?.name,
              name: format?.name,
            })}
          </Typography>
        );
        return isPast ? (
          <Fragment key={t}>
            <Button
              variant="text"
              role="button"
              sx={{ p: 0 }}
              onClick={() => navigateBack(tabStack.length - idx - 1)}
            >
              {titleStr}
            </Button>
            <Box sx={{ color: 'secondary.main', display: 'inline-flex', verticalAlign: 'middle' }}>
              <KeyboardArrowRightIcon />
            </Box>
          </Fragment>
        ) : (
          titleStr
        );
      }),
    [app?.name, format, navigateBack, tabStack],
  );

  const closeDrawer = () => {
    setTabStack(['home']);
    setFiles(null);
    dispatch(hrBotDrawer.close());
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={closeDrawer}>
      <Box
        sx={theme => ({
          display: 'flex',
          width: '100%',
          [theme.breakpoints.up('md')]: {
            width: '95vw',
            maxWidth: 1280,
          },
        })}
      >
        <Box sx={{ pt: '20px', pl: '20px' }}>
          {tabStack.length === 1 ? (
            <CloseButton onClick={closeDrawer} />
          ) : (
            <BackButton onClick={navigateBack} />
          )}
        </Box>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
          role="none"
        >
          <Container component="main" sx={{ flex: 1, display: 'flex' }} maxWidth={false}>
            <Box
              sx={{
                fontSize: 20,
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                height: '100vh',
                overflow: 'scroll',
              }}
            >
              {app && formats && formatsLoaded ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>{title}</Box>
                  <HrBotContext.Provider
                    value={{
                      bulkMessaging,
                      closeDrawer,
                      currentFile,
                      files,
                      format,
                      formatId,
                      formats,
                      message,
                      navigateBack,
                      navigateTo,
                      setCurrentFile,
                      setFiles,
                      setFormatId,
                      setMessage,
                      setUploadedPct,
                      tab,
                      uploadedPct,
                      checkIds,
                      droppedFiles,
                      setDroppedFiles,
                    }}
                  >
                    <HrBotTab tab={tab} />
                  </HrBotContext.Provider>
                </>
              ) : (
                <Loading />
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    </Drawer>
  );
};

export default memo(HrBotDrawer);
